import React from "react";
import { Form } from "@components";
import { Wrapper } from "../wrapper";
import * as Api from "@api";
import { FormSalesforceResponse } from "@api/types/form";
import { Box, Grid } from "@material-ui/core";
import { Warning as WarningIcon } from "@material-ui/icons";

export type SalesforceFormValues = {
  name: string;
  email: string;
  timezone: string;
  currency: string;
  geography: string;
  department: string;
  gpnIdlogin: string;
  setupType1: boolean;
  setupType2: boolean;
  setupType3: boolean;
  setupType4: boolean;
  setupType5: boolean;
  envQA1: boolean;
  envQA2: boolean;
  envQA3: boolean;
  envCert1: boolean;
  envCert2: boolean;
  envProduction: boolean;
  envDevelopment: boolean;
  regionAP: boolean;
  regionCA: boolean;
  regionUK: boolean;
  regionUS: boolean;
  envGlobal: boolean;
  insideFirstName: string;
  insideLastName: string;
  insidePhoneNumber: string;
  repCodeISR: string;
  repCodeUser: string;
  userType: string;
  profile: string;
  role: string;
  purposeHybrid: string;
  assign1: string;
  assign2: string;
  assign3: string;
  assign4: string;
  assign5: string;
  assign6: string;
  assign7: string;
  assign8: string;
  assign9: string;
  assign10: string;
  permission1: boolean;
  permission2: boolean;
  permission3: boolean;
  permission4: boolean;
  permission5: boolean;
  permission6: boolean;
  permission7: boolean;
  permission8: boolean;
  permission9: boolean;
  permission10: boolean;
};

type Key = keyof SalesforceFormValues;

type Props = {
  onCancel: () => void;
  onSubmit: (values: SalesforceFormValues) => void;
  open: boolean;
  values: Partial<SalesforceFormValues>;
  readonly?: boolean;
};

export const SalesforceForm: React.FC<Props> = ({
  onCancel,
  onSubmit,
  open,
  values,
  readonly,
}) => {
  const form = Form.useManager<SalesforceFormValues>({
    name: "",
    email: "",
    timezone: "",
    currency: "",
    geography: "",
    department: "",
    gpnIdlogin: "",
    setupType1: false,
    setupType2: false,
    setupType3: false,
    setupType4: false,
    setupType5: false,
    envQA1: false,
    envQA2: false,
    envQA3: false,
    envCert1: false,
    envCert2: false,
    envProduction: false,
    envDevelopment: false,
    regionAP: false,
    regionCA: false,
    regionUK: false,
    regionUS: false,
    envGlobal: false,
    insideFirstName: "",
    insideLastName: "",
    insidePhoneNumber: "",
    repCodeISR: "",
    repCodeUser: "",
    userType: "",
    profile: "",
    role: "",
    purposeHybrid: "",
    assign1: "",
    assign2: "",
    assign3: "",
    assign4: "",
    assign5: "",
    assign6: "",
    assign7: "",
    assign8: "",
    assign9: "",
    assign10: "",
    permission1: false,
    permission2: false,
    permission3: false,
    permission4: false,
    permission5: false,
    permission6: false,
    permission7: false,
    permission8: false,
    permission9: false,
    permission10: false,
    ...values,
  });

  const [fetching, setFetching] = React.useState(false);
  const [data, setData] = React.useState<FormSalesforceResponse>();
  const userTypeOptions = React.useMemo(() => {
    return data ? data.userTypes.map((userType) => userType.accessType) : [];
  }, [data]);
  const userProfileOptions = React.useMemo(() => {
    if (data && form.values.userType) {
      const userType = data.userTypes.find(
        (userType) => userType.accessType === form.values.userType
      );

      return userType ? userType.profiles : [];
    }

    return [];
  }, [data, form.values.userType]);
  const roleOptions = React.useMemo(() => {
    return data ? data.roles : [];
  }, [data]);

  React.useEffect(() => {
    setFetching(true);
    Api.getFormSalesForce()
      .then((data) => {
        setFetching(false);
        setData(data);
      })
      .catch((error) => {
        setFetching(false);
        console.log("error", error);
      });
  }, []);

  function onFormValueChange(
    name: keyof SalesforceFormValues,
    value: any
  ): void {
    switch (name) {
      case "setupType1":
      case "setupType2":
      case "setupType3":
      case "setupType4":
      case "setupType5":
        form.set(
          Form.target.VALUES,
          Object.assign(
            {
              setupType1: false,
              setupType2: false,
              setupType3: false,
              setupType4: false,
              setupType5: false,
            },
            {
              [name]: value,
            }
          )
        );
        break;
      case "userType":
        if (value === "Sales Profile with CSP Access") {
          form.set(Form.target.VALUE, ["permission2", true]);
        } else {
          form.set(Form.target.VALUE, ["permission2", false]);
          form.set(Form.target.VALUE, ["purposeHybrid", ""]);
        }
        if (value === "Docusign Access Only") {
          form.set(Form.target.VALUE, ["role", ""]);
        }
        form.set(Form.target.VALUE, ["profile", ""]);
        form.set(Form.target.VALUE, [name, value]);
        break;
      default:
        form.set(Form.target.VALUE, [name, value]);
        break;
    }
    validate(name, value).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  function validate(
    name: Key,
    value: SalesforceFormValues[typeof name]
  ): Promise<[Key, Error?]> {
    switch (name) {
      case "purposeHybrid":
        return Promise.resolve([
          name as Key,
          form.values.userType !== "Sales Profile with CSP Access"
            ? undefined
            : Form.Validators.hasValue(value),
        ]);
      case "profile":
      case "role":
        return Promise.resolve([
          name as Key,
          form.values.userType === "Docusign Access Only"
            ? undefined
            : Form.Validators.hasValue(value),
        ]);
      case "name":
      case "email":
      case "department":
      case "gpnIdlogin":
      case "userType":
      case "geography":
      case "timezone":
      case "currency":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function onFormSubmit(): void {
    Promise.all(
      Object.entries(form.values).map(([name, value]) =>
        validate(name as Key, value)
      )
    ).then((validations) => {
      const valid = validations.reduce((valid, [name, error]) => {
        return valid && !error;
      }, true);
      if (valid) {
        onSubmit(form.values);
      } else {
        validations.forEach(([name, error]) => {
          form.set(Form.target.ERROR, [name, error]);
        });
      }
    });
  }

  return (
    <Wrapper
      onCancel={() => {
        onCancel();
        if (values) {
          form.set(Form.target.VALUES, values);
        } else {
          form.reset();
        }
      }}
      onSubmit={onFormSubmit}
      open={open}
      size="md"
      title="Salesforce User Access Forms For Internal User"
      readonly={readonly}
    >
      <Form>
        <Form.Text color="warning.main">
          <Box display="flex">
            <WarningIcon />
            <Box pl={1} pt={0.5}>
              <Box pt={1}>
                Complete this form with true, complete and accurate information
                so that access request will be granted without delay.
              </Box>
            </Box>
          </Box>
        </Form.Text>
        <Form.Title>1 - User Information</Form.Title>
        <Form.Textbox
          error={form.errors.gpnIdlogin}
          label="GPN ID/Login"
          name="gpnIdlogin"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.gpnIdlogin}
        />
        <Form.Dropdown
          label="Geography"
          onChange={onFormValueChange}
          options={[
            "Hong Kong",
            "China",
            "Macau",
            "Malaysia",
            "Maldives",
            "India",
            "Philippines",
            "Singapore",
            "Sri Lanka",
            "Brunei",
            "Taiwan",
          ]}
          name="geography"
          size={6}
          value={form.values.geography || ""}
          error={form.errors.geography}
          required
        />
        <Form.Textbox
          error={form.errors.name}
          label="Job Title"
          name="name"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.name}
        />
        <Form.Dropdown
          label="Timezone"
          onChange={onFormValueChange}
          options={[
            "GMT+14:00 - Line Is. Time (Pacific/Kiritimati)",
            "GMT+13:00 - Phoenix Is. Time (Pacific/Enderbury)",
            "GMT+13:00 - Tonga Time (Pacific/Tongatapu)",
            "GMT+12:45 - Chatham Standard Time (Pacific/Chatham)",
            "GMT+12:00 - New Zealand Time (Pacific/Auckland)",
            "GMT+12:00 - Fiji Time (Pacific/Fiji)",
            "GMT+12:00 - Petropavlovsk-Kamchatski Time (Asia/Kamachatka)",
            "GMT+11:30 - Norfolk Time (Pacific/Norfolk)",
            "GMT+11:00 - Lord Howe Standard Time (Australia/Lord_Howe)",
            "GMT+11:00 - Solomon Is. Time (Pacific/Guadalcanal)",
            "GMT+10:30 - Australian Central Standard Time ((South Australia) Australia/Adelaide)",
            "GMT+10:00 - Australian Eastern Standard Time (New South Wales)(Australia/Brisbane)",
            "GMT+10:00 - Australian Eastern Standard Time (Queensland)(Australia/Brisbane)",
            "GMT+09:30 - Australian Central Standard Time (Northern Territory)(Australia/Darwin)",
            "GMT+09:00 - Korea Standard Time (Asia/Seoul)",
            "GMT+09:00 - Japan Standard Time (Asia/Toyko)",
            "GMT+08:00 - Hong Kong Time (Asia/Hong_Kong)",
            "GMT+08:00 - Malyasia Time (Asia/Kuala_Lumpur)",
            "GMT+08:00 - Philippines Time (Asia/Manila)",
            "GMT+08:00 - China Standard Time (Asia/Shangai)",
            "GMT+08:00 - Singapore Time (Asia/Singapore)",
            "GMT+08:00 - China Standard Time (Asia/Taipei)",
            "GMT+08:00 - Australian Western Standard time (Australia/Perth)",
            "GMT+07:00 - Indochina Time (Asia/Bangkok)",
            "GMT+07:00 - Indochina Time (Asia/Ho_Chi_Minh)",
            "GMT+07:00 - West Indonesia Time (Asia/Jakarta)",
            "GMT+06:30 - Myanmar Time (Asia/Rangoon)",
            "GMT+06:00 - Bangladesh Time (Asia/Dhaka)",
            "GMT+05:45 - Nepal Time (Asia/Kathmandu)",
            "GMT+05:30 - India Standard Time (Asia/Colombo)",
            "GMT+05:30 - India Standard Time (Asia/Kolkata)",
            "GMT+05:00 - Pakistan Time (Asia/Karachi)",
            "GMT+05:00 - Uzbekistan Time (Asia/Tashkent)",
            "GMT+05:00 - Yekateringburg Time (Asia/Yekaterinburg)",
            "GMT+04:30 - Afghanistan Time (Asia/Kabul)",
            "GMT+04:00 - Azerbaijan Summer Time (Asia/Baku)",
            "GMT+04:00 - Gulf Standard Time (Asia/Dubai)",
            "GMT+04:00 - Georgia Time (Asia/Tbilisi)",
            "GMT+04:00 - Armenia Time (Asia/Yerevan)",
            "GMT+03:30 - Iran Daylight Time (Asian/Tehran)",
            "GMT+03:30 - East African Time (Africa/Nairobi)",
            "GMT+03:00 - Arabia Standard Time (Asia/Baghdad)",
            "GMT+03:00 - Arabia Standard Time (Asia/Kuwait)",
            "GMT+03:00 - Arabia Standard Time (Asia/Riyadh)",
            "GMT+03:00 - Moscow Standard Time (Europe/Minsk)",
            "GMT+03:00 - Moscow Standard Time (Europe/Moscow)",
            "GMT+03:00 - Eastern European Summer Time (Africa/Cairo)",
            "GMT+03:00 - Eastern European Summer Time (Asia/Beirut)",
            "GMT+03:00 - Israel Daylight Time (Asia/Jerusalem)",
            "GMT+03:00 - Eastern European Summer Time (Europe/Athens)",
            "GMT+03:00 - Eastern European Summer Time (Europe/Bucharest)",
            "GMT+03:00 - Eastern European Summer Time (Europe/Helsinki)",
            "GMT+03:00 - Eastern European Summer Time (Europe/Istanbul)",
            "GMT+02:00 - South Africa Standard Time (Africa/Johannesburg)",
            "GMT+02:00 - Central European Summer Time (Europe/Amsterdam)",
            "GMT+02:00 - Central European Summer Time (Europe/Berlin)",
            "GMT+02:00 - Central European Summer Time (Europe/Brussels)",
            "GMT+02:00 - Central European Summer Time (Europe/Paris)",
            "GMT+02:00 - Central European Summer Time (Europe/Prague)",
            "GMT+02:00 - Central European Summer Time (Europe/Rome)",
            "GMT+02:00 - Western European Summer Time (Europe/Lisbon)",
            "GMT+01:00 - Central European Time (Africa/Algiers)",
            "GMT+01:00 - British Summer Time (Europe/London)",
            "GMT-01:00 - Cape Verde Time (Atlantic/Cape_Verde) ",
            "GMT+00:00 - Western European Time (Africa/Casablanca)",
            "GMT+00:00 - Irish Summer Time (Europe/Dublin)",
            "GMT+00:00 - Greenwich Mean Time (GMT)",
            "GMT-00:00 - Eastern Greenland Summer Time (America/Scoresbysund)",
            "GMT-00:00 - Azores Summer Time (Atlantic/Azores) ",
            "GMT-02:00 - South Georgia Standard Time (Atlantic/South_Georgia) ",
            "GMT-02:30 - Newfoundland Daylight Time (America/St_Johns)",
            "GMT-03:00 - Brasilia Summer Time (America/Sao_Paulo)",
            "GMT-03:00 - Argentina Time (America/Argentina/Buenos Aires)",
            "GMT-03:00 - Chile Summer Time (America/Santiago)",
            "GMT-03:00 - Atlantic Daylight Time (America/Halifax)",
            "GMT-04:00 - Atlantic Standard Time (America/Puerto_Rico)",
            "GMT-04:00 - Atlantic Daylight Time (Atlantic/Bermuda)",
            "GMT-04:30 - Venezuela Time (America/Caracas)",
            "GMT-04:00 - Eastern Daylight Time (America/Indiana/Indianapolis)",
            "GMT-04:00 - Eastern Daylight Time (America/New_York)",
            "GMT-05:00 - Colombia Time (America/Bogota)",
            "GMT-05:00 - Peru Time (America/Lima)",
            "GMT-05:00 - Eastern Standard Time (America/Panama)",
            "GMT-05:00 - Central Daylight Time (America/Mexico_City)",
            "GMT-05:00 - Central Daylight Time (America/Chicago)",
            "GMT-06:00 - Central Standard Time (America/El_Salvador)",
            "GMT-06:00 - Mountain Daylight Time (America/Denver)",
            "GMT-06:00 - Mountain Standard Time (America/Mazatlan)",
            "GMT-07:00 - Mountain Standard Time (America/Phoenix)",
            "GMT-07:00 - Pacific Daylight Time (America/Los_Angeles)",
            "GMT-07:00 - Pacific Daylight Time (America/Tijuana)",
            "GMT-08:00 - Pitcairn Standard Time (Pacific/Pitcairn)",
            "GMT-08:00 - Alaska Daylight Time (America/Anchorage)",
            "GMT-09:00 - Gambier Time (Pacific/Gambier)",
            "GMT-09:00 - Hawaii-Aleutian Standard Time (America/Adak)",
            "GMT-09:30 - Marquesas Time (Pacific/Marquesas)",
            "GMT-10:00 - Hawaii-Aleutian Standard Time (Pacific/Honolulu)",
            "GMT-11:00 - Niue Time (Pacific/Niue)",
            "GMT-11:00 - Samoa Standard Time (Pacific/Pago_Pago)",
          ]}
          name="timezone"
          size={6}
          value={form.values.timezone || ""}
          error={form.errors.timezone}
          required
        />
        <Form.Textbox
          error={form.errors.email}
          label="Manager"
          name="email"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.email}
        />
        <Form.Dropdown
          label="Currency"
          onChange={onFormValueChange}
          options={[
            "CAD - Canadian Dollar",
            "CNY - Chinese Yuan",
            "EUR - Euro",
            "GBP - British Pound",
            "HKD - Hong Kong Dollar",
            "INR - Indian Rupee",
            "LKR - Sri Lanka Rupee",
            "MOP - Macau Pataca",
            "MXN - Mexican Peso",
            "MYR - Malyasian Ringgit",
            "PHP - Philippine Peso",
            "SGD - Singapore Dollar",
            "TWD - Taiwan Dollar",
            "USD - U.S. Dollar",
          ]}
          name="currency"
          size={6}
          value={form.values.currency || ""}
          error={form.errors.currency}
          required
        />
        <Form.Textbox
          error={form.errors.department}
          label="Department"
          name="department"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.department}
        />
        <Form.Title>2 - Setup Type</Form.Title>
        <Form.Radiobutton
          label="New User Setup"
          name="setupType1"
          onChange={onFormValueChange}
          size={4}
          value={form.values.setupType1}
        />
        <Form.Radiobutton
          label="Reducing Access"
          name="setupType2"
          onChange={onFormValueChange}
          size={4}
          value={form.values.setupType2}
        />
        <Form.Radiobutton
          label="Additional Access / Access Modification"
          name="setupType3"
          onChange={onFormValueChange}
          size={4}
          value={form.values.setupType3}
        />
        <Form.Radiobutton
          label="Deactivate User"
          name="setupType4"
          onChange={onFormValueChange}
          size={4}
          value={form.values.setupType4}
        />
        <Form.Radiobutton
          label="Reactivate"
          name="setupType5"
          onChange={onFormValueChange}
          size={4}
          value={form.values.setupType5}
        />
        {/* <Form.Title>3 - Environment</Form.Title>
        <Form.Checkbox
          label="QA1"
          name="envQA1"
          onChange={onFormValueChange}
          size={3}
          value={form.values.envQA1}
        />
        <Form.Checkbox
          label="QA2"
          name="envQA2"
          onChange={onFormValueChange}
          size={3}
          value={form.values.envQA2}
        />
        <Form.Checkbox
          label="QA3"
          name="envQA3"
          onChange={onFormValueChange}
          size={3}
          value={form.values.envQA3}
        />
        <Form.Checkbox
          label="CERT1"
          name="envCert1"
          onChange={onFormValueChange}
          size={3}
          value={form.values.envCert1}
        />
        <Form.Checkbox
          label="CERT2"
          name="envCert2"
          onChange={onFormValueChange}
          size={3}
          value={form.values.envCert2}
        />
        <Form.Checkbox
          label="Production"
          name="envProduction"
          onChange={onFormValueChange}
          size={3}
          value={form.values.envProduction}
        />
        <Form.Checkbox
          label="Development"
          name="envDevelopment"
          onChange={onFormValueChange}
          size={3}
          value={form.values.envDevelopment}
        />
        <Form.Title>4 - Region</Form.Title>
        <Form.Checkbox
          label={{
            text: "Asia Pacific (AP)",
            tooltip:
              "If this is checkbox is marked with 'X', add the user to the Public Group \"Regional Alignment - AP CC (no India)\"",
          }}
          name="regionAP"
          onChange={onFormValueChange}
          size={3}
          value={form.values.regionAP}
        />
        <Form.Checkbox
          label={{
            text: "Canada (CA)",
            tooltip:
              'If this is checkbox is marked with \'X\', add the user to the Public Group: "Regional Alignment - CA" AND "Regional Alignment US""',
          }}
          name="regionCA"
          onChange={onFormValueChange}
          size={3}
          value={form.values.regionCA}
        />
        <Form.Checkbox
          label={{
            text: "Europe (UK)",
            tooltip:
              "If this is checkbox is marked with 'X', add the user to the Public Group \"Regional Alignment UK\"",
          }}
          name="regionUK"
          onChange={onFormValueChange}
          size={3}
          value={form.values.regionUK}
        />
        <Form.Checkbox
          label={{
            text: "United States (US)",
            tooltip:
              "If this is checkbox is marked with 'X', add the user to the Public Group \"Regional Alignment - AP CC (no India)\"",
          }}
          name="regionUS"
          onChange={onFormValueChange}
          size={3}
          value={form.values.regionUS}
        />
        <Form.Checkbox
          label={{
            text: "Global",
            tooltip:
              "If this is checkbox is marked with 'X', add the user to the Global regional alignment/s",
          }}
          name="envGlobal"
          onChange={onFormValueChange}
          size={3}
          value={form.values.envGlobal}
        />
        <Form.Title>
          4.5 - Inside Sales Rep Information (Canada and UK user only)
        </Form.Title>
        <Form.Text color="warning.main">
          <Box display="flex">
            <WarningIcon />
            <Box pl={1} pt={0.5}>
              Only fill out this section if the CA/UK user will require
              integration
            </Box>
          </Box>
        </Form.Text>
        <Form.Textbox
          label="First Name"
          name="insideFirstName"
          onChange={onFormValueChange}
          size={6}
          value={form.values.insideFirstName}
        />
        <Form.Textbox
          label="Rep Code of ISR"
          name="repCodeISR"
          onChange={onFormValueChange}
          size={6}
          value={form.values.repCodeISR}
        />
        <Form.Textbox
          label="Last Name"
          name="insideLastName"
          onChange={onFormValueChange}
          size={6}
          value={form.values.insideLastName}
        />
        <Form.Textbox
          label="Rep Code of User"
          name="repCodeUser"
          onChange={onFormValueChange}
          size={6}
          value={form.values.repCodeUser}
        />
        <Form.Textbox
          label="Phone Number"
          name="insidePhoneNumber"
          onChange={onFormValueChange}
          size={6}
          value={form.values.insidePhoneNumber}
        /> */}
        <Form.Title>3 - Role and Persona Assignment</Form.Title>
        <Form.Text color="warning.main">
          <Box display="flex">
            <WarningIcon />
            <Box pl={1} pt={0.5}>
              Choosing "Sales Profile with CSP Access" means assigning Sales
              Profile and adding "SE CSP Permission Set" to the User. These are
              known as "Hybrid users" and needs additional approval from
              Application Owner/Director. A reason or purpose of the Hybrid
              Access is required.
            </Box>
          </Box>
        </Form.Text>
        <Grid container spacing={2} item xs={6}>
          <Form.Dropdown
            label={{
              text: "User Type",
              tooltip:
                "Sales - Leads, Opportunities, Quotes (GPN Direct Sales)\n" +
                "\n" +
                "CSP - Accounts, Incidents, Terminals (GP Call Center)\n" +
                "\n" +
                "Sales with CSP - GPN Direct Sales and GP Call Center",
            }}
            name="userType"
            onChange={onFormValueChange}
            options={userTypeOptions}
            size={12}
            value={form.values.userType}
            error={form.errors.userType}
            disabled={fetching}
            required
            loading={fetching}
          />
          <Form.Dropdown
            label={{
              text: "Persona",
              tooltip:
                "Please specify the User type above which will determine the appropriate Persona options.",
            }}
            name="profile"
            onChange={onFormValueChange}
            options={userProfileOptions}
            size={12}
            value={form.values.profile}
            error={form.errors.profile}
            disabled={fetching}
            required={form.values.userType !== "Docusign Access Only"}
            loading={fetching}
          />
          <Form.Dropdown
            label={{
              text: "Role",
              tooltip:
                'Instructions: Select a region in Section 1 and then select User Type in Section 3. The role selection will vary depending what you select in the previously mentioned fields. (See "Role Hierarchy" tab for reference.)',
            }}
            name="role"
            onChange={onFormValueChange}
            options={roleOptions}
            size={12}
            value={form.values.role}
            error={form.errors.role}
            disabled={
              fetching || form.values.userType === "Docusign Access Only"
            }
            required={form.values.userType !== "Docusign Access Only"}
            loading={fetching}
          />
        </Grid>
        <Grid container spacing={2} item xs={6}>
          <Form.Textarea
            error={form.errors.purposeHybrid}
            label={{
              text: "Purpose of Hybrid Access",
              caption:
                form.values.userType === "Sales Profile with CSP Access"
                  ? "Vivek Pujeri"
                  : undefined,
            }}
            name="purposeHybrid"
            onChange={onFormValueChange}
            placeholder="Write details here"
            rows={5}
            rowsMax={5}
            size={12}
            value={form.values.purposeHybrid}
            disabled={form.values.userType !== "Sales Profile with CSP Access"}
            required={form.values.userType === "Sales Profile with CSP Access"}
          />
        </Grid>
        <Form.Title subtitle="The user should be added to the corresponding Regional Alignment Group depending on which Region he belongs to.">
          4 - Group/Queue Assignments
        </Form.Title>
        <Form.Textbox
          label="1"
          name="assign1"
          onChange={onFormValueChange}
          size={6}
          value={form.values.assign1}
        />
        <Form.Textbox
          label="6"
          name="assign6"
          onChange={onFormValueChange}
          size={6}
          value={form.values.assign6}
        />
        <Form.Textbox
          label="2"
          name="assign2"
          onChange={onFormValueChange}
          size={6}
          value={form.values.assign2}
        />
        <Form.Textbox
          label="7"
          name="assign7"
          onChange={onFormValueChange}
          size={6}
          value={form.values.assign7}
        />
        <Form.Textbox
          label="3"
          name="assign3"
          onChange={onFormValueChange}
          size={6}
          value={form.values.assign3}
        />
        <Form.Textbox
          label="8"
          name="assign8"
          onChange={onFormValueChange}
          size={6}
          value={form.values.assign8}
        />
        <Form.Textbox
          label="4"
          name="assign4"
          onChange={onFormValueChange}
          size={6}
          value={form.values.assign4}
        />
        <Form.Textbox
          label="9"
          name="assign9"
          onChange={onFormValueChange}
          size={6}
          value={form.values.assign9}
        />
        <Form.Textbox
          label="5"
          name="assign5"
          onChange={onFormValueChange}
          size={6}
          value={form.values.assign5}
        />
        <Form.Textbox
          label="10"
          name="assign10"
          onChange={onFormValueChange}
          size={6}
          value={form.values.assign10}
        />
        {/* <Form.Title>7 - Other Permissions/Acceses</Form.Title>
        <Form.Checkbox
          label="Steelbrick"
          name="permission1"
          onChange={onFormValueChange}
          size={4}
          value={form.values.permission1}
        />
        <Form.Checkbox
          label="SE CSP Permission Set"
          name="permission2"
          onChange={onFormValueChange}
          size={4}
          value={form.values.permission2}
        />
        <Form.Checkbox
          label="Opportunity - Merchant ID Read/Write Access"
          name="permission3"
          onChange={onFormValueChange}
          size={4}
          value={form.values.permission3}
        />
        <Form.Checkbox
          label="Contest Participant Salesforce"
          name="permission4"
          onChange={onFormValueChange}
          size={4}
          value={form.values.permission4}
        />
        <Form.Checkbox
          label="Scorecard Manager"
          name="permission5"
          onChange={onFormValueChange}
          size={4}
          value={form.values.permission5}
        />
        <Form.Checkbox
          label="SCB Administrator Salesforce"
          name="permission6"
          onChange={onFormValueChange}
          size={4}
          value={form.values.permission6}
        />
        <Form.Checkbox
          label="Docusign"
          name="permission7"
          onChange={onFormValueChange}
          size={4}
          value={form.values.permission7}
        />
        <Form.Checkbox
          label="Knowledge User"
          name="permission8"
          onChange={onFormValueChange}
          size={4}
          value={form.values.permission8}
        />
        <Form.Checkbox
          label="Scorecard Admin"
          name="permission9"
          onChange={onFormValueChange}
          size={4}
          value={form.values.permission9}
        />
        <Form.Checkbox
          label="Scorecard User"
          name="permission10"
          onChange={onFormValueChange}
          size={4}
          value={form.values.permission10}
        /> */}
      </Form>
    </Wrapper>
  );
};
